import Colors from '../../../../utils/Colors.js'

// Building blocks of every e-mail, in the style of the Track & Trace page (#3845): a header with the logo on white and
// a band in the company colour, an optional photo, then cards with fields, a code, attachments and a button, and an
// optional footer. Tables and inline styles so the mails work in Gmail, Outlook (with a VML button) and Apple Mail.
// The templates are also rendered in the settings as a preview inside the app, so the <style> rules only use vl-
// classes. The blocks don't escape: the templates get their values escaped by escapedTemplate.

export const FONT = 'Roboto, Arial, Helvetica, sans-serif'

export const mailColors = {
    ground: '#FAFAFA',
    surface: '#FFFFFF',
    text: '#646464',
    ink: '#242424',
    line: '#ECECEC',
    code: '#F2F2F2',
    warn: '#E08600',
    warnBackground: '#FFF4E5',
    warnInk: '#7A4100',
    fallback: '#1F2D3D'
}

const env = (name: string): string | undefined => typeof process !== 'undefined' ? process.env?.[name] : undefined

/** Origin for images in the mail: the given base url (the preview in the app), else the host of the server. */
export function hostUrl(baseUrl?: string): string {
    return (baseUrl || env('HOST_URL') || 'https://app.veloyd.nl').replace(/\/+$/, '')
}

/** Absolute url of an image: data URIs and absolute urls stay as they are, a path gets the host in front. */
export function imageUrl(src: string | null | undefined, baseUrl?: string): string {
    if (!src) {
        return ''
    }

    if (/^(data:|https?:\/\/)/i.test(src)) {
        return src
    }

    return `${hostUrl(baseUrl)}/${src.replace(/^\/+/, '')}`
}

export function iconUrl(name: string, baseUrl?: string): string {
    return `${hostUrl(baseUrl)}/images/icons/mail/${name}.png`
}

/** Domain of the links in a mail of the reseller: its own domain with custom branding, else the app. */
export function mailDomain(reseller?: {customBrandingEnabled?: boolean, settings?: {domain?: string}}): string {
    if (reseller?.customBrandingEnabled && reseller.settings?.domain) {
        return reseller.settings.domain
    }

    return env('NODE_ENV') === 'production' ? 'app.veloyd.nl' : 'test.veloyd.nl'
}

export function buttonTextColor(color: string): string {
    return Colors.isDarkBackground(color) ? Colors.white : Colors.textDark
}

/** Text from a settings editor: line breaks become <br/>. */
export function multiline(text: string | null | undefined): string {
    return (text || '').replace(/\n/g, '<br/>')
}

type AddressLike = {name?: string, attention?: string, street?: string, nr?: string, addition?: string, street2?: string, postalCode?: string, city?: string, country?: string}

/** The lines of an address, empty lines left out. */
export function addressLines(address: AddressLike | null | undefined, {name = true, country = true} = {}): string {
    if (!address) {
        return ''
    }

    return [
        name ? address.name : '',
        name ? address.attention : '',
        [address.street, `${address.nr || ''}${address.addition || ''}`].filter(Boolean).join(' '),
        address.street2,
        [address.postalCode, address.city].filter(Boolean).join(' '),
        country ? address.country : ''
    ].filter((line) => line && line.trim()).join('<br/>')
}

const table = (content: string, style = '', attributes = '') => `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ${attributes} style="width: 100%; border-collapse: collapse; mso-table-lspace: 0pt; mso-table-rspace: 0pt; ${style}">${content}</table>`

const textStyle = (size = 14, lineHeight = 20, color = mailColors.ink, extra = '') => `font-family: ${FONT}; font-size: ${size}px; line-height: ${lineHeight}px; color: ${color}; ${extra}`

/** Blocks under each other with space between them; empty blocks are left out. */
export function stack(blocks: (string | false | null | undefined)[], gap = 16): string {
    const filled = blocks.filter(Boolean) as string[]

    return table(filled.map((block, index) => `<tr><td style="padding: 0 0 ${index === filled.length - 1 ? 0 : gap}px 0;">${block}</td></tr>`).join(''))
}

export function text(html: string, {size = 14, lineHeight = 20, color = mailColors.ink, bold = false, align = 'left'} = {}): string {
    return `<div style="${textStyle(size, lineHeight, color, `font-weight: ${bold ? 500 : 400}; text-align: ${align};`)}">${html}</div>`
}

/** The greeting and message of the mail, on the ground above the cards. */
export function intro(html: string): string {
    return html ? text(html, {size: 16, lineHeight: 24}) : ''
}

export function heading(title: string): string {
    return text(title, {size: 16, lineHeight: 24, bold: true})
}

export function link(href: string, label: string, color: string): string {
    return `<a href="${href}" target="_blank" style="color: ${color}; text-decoration: underline; font-family: ${FONT};">${label}</a>`
}

export function divider(): string {
    return `<div style="height: 1px; line-height: 1px; font-size: 1px; background-color: ${mailColors.line};">&nbsp;</div>`
}

/** A white card with its content under each other. */
export function card(content: (string | false | null | undefined)[], {padding = 20, gap = 16} = {}): string {
    return table(
        `<tr><td class="vl-card" style="padding: ${padding}px;">${stack(content, gap)}</td></tr>`,
        `background-color: ${mailColors.surface}; border: 1px solid ${mailColors.line}; border-radius: 12px; border-collapse: separate;`,
        `bgcolor="${mailColors.surface}"`
    )
}

/** A label with its value; with an icon in front for the rows of the status card. */
export function field(label: string, value: string, icon?: string): string {
    if (!value && value !== '0') {
        return ''
    }

    const body = `
        <div style="${textStyle(12, 16, mailColors.text, 'letter-spacing: 0.4px;')}">${label}</div>
        <div style="${textStyle(14, 20, mailColors.ink, 'word-break: break-word;')}">${value}</div>`

    if (!icon) {
        return body
    }

    return table(`<tr>
        <td width="32" valign="top" style="width: 32px; padding: 0 12px 0 0;"><img src="${icon}" width="20" height="20" alt="" style="display: block; width: 20px; height: 20px; border: 0;"></td>
        <td valign="top">${body}</td>
    </tr>`)
}

/** A verification or delivery code in a grey box. */
export function codeBox(code: string, label = ''): string {
    if (!code) {
        return ''
    }

    return table(
        `<tr><td style="padding: 12px 16px;">
            ${label ? `<div style="${textStyle(12, 16, mailColors.text, 'letter-spacing: 0.4px;')}">${label}</div>` : ''}
            <div style="${textStyle(24, 32, mailColors.ink, 'font-weight: 700; letter-spacing: 3px;')}">${code}</div>
        </td></tr>`,
        `background-color: ${mailColors.code}; border-radius: 8px; border-collapse: separate;`,
        `bgcolor="${mailColors.code}"`
    )
}

/** The name of a file attached to the mail. */
export function attachment(filename: string, baseUrl?: string): string {
    if (!filename) {
        return ''
    }

    return table(
        `<tr>
            <td width="32" valign="middle" style="width: 32px; padding: 10px 0 10px 12px;"><img src="${iconUrl('paperclip', baseUrl)}" width="20" height="20" alt="" style="display: block; width: 20px; height: 20px; border: 0;"></td>
            <td valign="middle" style="padding: 10px 12px 10px 8px; ${textStyle(14, 20)}">${filename}</td>
        </tr>`,
        `border: 1px solid ${mailColors.line}; border-radius: 8px; border-collapse: separate;`
    )
}

/** A note with an icon, e.g. the requirements of a shipment; warn for what the recipient has to act on. */
export function notice(lines: string[], {title = '', warn = false, baseUrl = ''} = {}): string {
    const filled = lines.filter(Boolean)

    if (!filled.length) {
        return ''
    }

    const background = warn ? mailColors.warnBackground : mailColors.code
    const color = warn ? mailColors.warnInk : mailColors.ink

    return table(
        `<tr>
            <td width="32" valign="top" style="width: 32px; padding: 12px 0 12px 12px;"><img src="${iconUrl('information-outline', baseUrl)}" width="20" height="20" alt="" style="display: block; width: 20px; height: 20px; border: 0;"></td>
            <td valign="top" style="padding: 12px 12px 12px 8px;">
                ${title ? `<div style="${textStyle(14, 20, color, 'font-weight: 500;')}">${title}</div>` : ''}
                ${filled.map((line) => `<div style="${textStyle(14, 20, color)}">${line}</div>`).join('')}
            </td>
        </tr>`,
        `background-color: ${background}; border-radius: 8px; border-collapse: separate;`,
        `bgcolor="${background}"`
    )
}

/** A button in the company colour, with a VML fallback for Outlook. Without a link it is shown but does nothing. */
export function button(label: string, href: string | null, color: string): string {
    const textColor = buttonTextColor(color)
    const target = href ? `href="${href}" target="_blank"` : ''

    return table(`<tr><td align="center">
        <!--[if mso]><v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" ${href ? `href="${href}"` : ''} style="height: 44px; v-text-anchor: middle; width: 300px;" arcsize="10%" stroke="f" fillcolor="${color}"><w:anchorlock/><center style="color: ${textColor}; font-family: Arial, sans-serif; font-size: 16px; font-weight: bold;">${label}</center></v:roundrect><![endif]-->
        <!--[if !mso]><!-- --><a class="vl-button" ${target} style="display: inline-block; min-width: 200px; padding: 12px 24px; border-radius: 4px; background-color: ${color}; color: ${textColor}; ${textStyle(16, 20, textColor, 'font-weight: 500; text-align: center; text-decoration: none; mso-hide: all;')}">${label}</a><!--<![endif]-->
    </td></tr>`)
}

/** Small grey lines at the bottom of the mail; the first line is the sender. */
export function footer(lines: string[]): string {
    const filled = lines.filter(Boolean)

    if (!filled.length) {
        return ''
    }

    return filled.map((line, index) => `<div style="${textStyle(12, 18, index === 0 ? mailColors.ink : mailColors.text, `padding-top: ${index === 0 ? 0 : 4}px; ${index === 0 ? 'font-weight: 500;' : ''}`)}">${line}</div>`).join('')
}

const css = `
.vl-mail a[x-apple-data-detectors] { color: inherit !important; text-decoration: none !important; }
@media only screen and (max-width: 620px) {
    .vl-container { width: 100% !important; max-width: 100% !important; }
    .vl-px { padding-left: 16px !important; padding-right: 16px !important; }
    .vl-card { padding: 16px !important; }
    .vl-button { display: block !important; min-width: 0 !important; }
    .vl-tile { display: block !important; width: 100% !important; padding: 0 0 8px 0 !important; }
    .vl-tile-link { padding: 10px 12px !important; text-align: left !important; }
    .vl-tile-label { display: inline-block !important; padding: 0 0 0 12px !important; vertical-align: middle; }
    .vl-headline { font-size: 22px !important; line-height: 28px !important; }
}`

export interface MailDocument {
    logo: string
    color?: string
    // Photo across the width of the mail, under the band
    photo?: string
    // The blocks of the mail, see stack()
    content: string
    footer?: string
    baseUrl?: string
}

/** The whole mail: header with the logo and the band in the company colour, the photo, the content and the footer. */
export function mailDocument({logo, color, photo, content, footer: footerHtml, baseUrl}: MailDocument): string {
    const accent = color || mailColors.fallback
    const logoSrc = imageUrl(logo, baseUrl)
    const photoSrc = imageUrl(photo, baseUrl)

    return `<!DOCTYPE html>
<html lang="nl" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
    <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="x-apple-disable-message-reformatting">
    <meta name="format-detection" content="telephone=no, date=no, address=no, email=no">
    <title></title>
    <!--[if mso]><noscript><xml><o:OfficeDocumentSettings><o:AllowPNG/><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript><![endif]-->
    <style type="text/css">${css}</style>
</head>
<body class="vl-mail" style="margin: 0; padding: 0; background-color: ${mailColors.ground}; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%;">
    ${table(`<tr><td align="center" style="padding: 0;">
        <!--[if mso]><table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0"><tr><td><![endif]-->
        <table role="presentation" class="vl-container" width="600" cellpadding="0" cellspacing="0" border="0" style="width: 600px; max-width: 600px; margin: 0 auto; border-collapse: collapse;">
            <tr><td class="vl-px" align="left" bgcolor="${mailColors.surface}" style="background-color: ${mailColors.surface}; padding: 20px 24px;">
                ${logoSrc ? `<img src="${logoSrc}" alt="Logo" style="display: block; max-height: 48px; max-width: 200px; width: auto; height: auto; border: 0;">` : ''}
            </td></tr>
            <tr><td height="6" bgcolor="${accent}" style="height: 6px; line-height: 6px; font-size: 6px; background-color: ${accent};">&nbsp;</td></tr>
            ${photoSrc ? `<tr><td style="font-size: 0; line-height: 0;"><img src="${photoSrc}" width="600" alt="" style="display: block; width: 100%; max-width: 600px; height: auto; border: 0;"></td></tr>` : ''}
            <tr><td class="vl-px" style="padding: 24px 24px ${footerHtml ? 8 : 32}px 24px;">${content}</td></tr>
            ${footerHtml ? `<tr><td class="vl-px" style="padding: 16px 24px 32px 24px;">${footerHtml}</td></tr>` : ''}
        </table>
        <!--[if mso]></td></tr></table><![endif]-->
    </td></tr>`, `background-color: ${mailColors.ground};`, `bgcolor="${mailColors.ground}"`)}
</body>
</html>`
}
